import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../services/supabase/Supabase";

export type RegionId = "au" | "gb";

export type Region = {
  id: RegionId;
  code: "AU" | "GB";
  label: string;
  baseUrl: string;
};

const PRIMARY_BASE_URL = import.meta.env.VITE_BASE_API_URL as string;
const GB_BASE_URL = import.meta.env.VITE_GB_BASE_API_URL as string | undefined;
const IS_PROD = import.meta.env.VITE_ENVIRONMENT === "production";

export const REGIONS: Region[] = IS_PROD && GB_BASE_URL
  ? [
      { id: "au", code: "AU", label: "AU", baseUrl: PRIMARY_BASE_URL },
      { id: "gb", code: "GB", label: "GB", baseUrl: GB_BASE_URL },
    ]
  : [{ id: "au", code: "AU", label: "AU", baseUrl: PRIMARY_BASE_URL }];

const STORAGE_KEY = "dashboards.region";

function decodeJwtRegionCodes(jwt: string | undefined): Set<string> {
  if (!jwt) return new Set();
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return new Set();
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { regions?: unknown };
    if (!Array.isArray(claims.regions)) return new Set();
    const codes = claims.regions
      .map((r) => {
        if (!r || typeof r !== "object") return null;
        const code = (r as { code?: unknown }).code;
        const role = (r as { role?: unknown }).role;
        if (typeof code !== "string") return null;
        if (role == null) return null;
        return code;
      })
      .filter((c): c is string => c !== null);
    return new Set(codes);
  } catch {
    return new Set();
  }
}

function allowedFromJwt(jwt: string | undefined): Set<RegionId> {
  const codes = decodeJwtRegionCodes(jwt);
  const allowed = new Set<RegionId>();
  for (const r of REGIONS) {
    if (codes.has(r.code)) allowed.add(r.id);
  }
  return allowed;
}

function loadInitialRegion(allowed: Set<RegionId>): Region {
  if (REGIONS.length === 1) return REGIONS[0]!;
  let saved: string | null = null;
  try { saved = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
  const savedMatch = REGIONS.find((r) => r.id === saved && allowed.has(r.id));
  if (savedMatch) return savedMatch;
  const firstAllowed = REGIONS.find((r) => allowed.has(r.id));
  if (firstAllowed) return firstAllowed;
  return REGIONS[0]!;
}

type RegionContextValue = {
  region: Region;
  setRegion: (id: RegionId) => void;
  regions: Region[];
  isAllowed: (id: RegionId) => boolean;
};

const RegionContext = createContext<RegionContextValue | null>(null);

export function RegionProvider({ children }: { children: ReactNode }) {
  const [allowed, setAllowed] = useState<Set<RegionId>>(() => new Set(REGIONS.map((r) => r.id)));
  const [region, setRegionState] = useState<Region>(() => loadInitialRegion(new Set(REGIONS.map((r) => r.id))));
  const [primed, setPrimed] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const next = allowedFromJwt(data.session?.access_token);
      setAllowed(next);
      if (!primed) {
        setRegionState(loadInitialRegion(next));
        setPrimed(true);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = allowedFromJwt(session?.access_token);
      setAllowed(next);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [primed]);

  const setRegion = useCallback((id: RegionId) => {
    const next = REGIONS.find((r) => r.id === id);
    if (!next) return;
    setRegionState((curr) => {
      if (curr.id === next.id) return curr;
      try { localStorage.setItem(STORAGE_KEY, next.id); } catch { /* ignore */ }
      queryClient.removeQueries({ queryKey: ["admin-query"] });
      return next;
    });
  }, [queryClient]);

  const isAllowed = useCallback((id: RegionId) => allowed.has(id), [allowed]);

  const value = useMemo<RegionContextValue>(
    () => ({ region, setRegion, regions: REGIONS, isAllowed }),
    [region, setRegion, isAllowed],
  );
  return createElement(RegionContext.Provider, { value }, children);
}

export function useRegion(): RegionContextValue {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error("useRegion must be used inside RegionProvider");
  return ctx;
}
